import {
  signInWithCustomToken as firebaseSignInWithCustomToken,
  type User,
} from 'firebase/auth'
import { authStore } from './authStore'
import { getFirebaseAuth } from './firebase'

/**
 * The client half of signing WeHunt Web in through natlink-sso.
 *
 * WeHunt Web is a public OAuth client: it has no secret it could keep, so the
 * authorization code it gets back is bound to this browser with PKCE, and the
 * code is exchanged cross-origin for a Firebase custom token that signs the
 * user in to the same Firebase project every other Natlink service uses.
 * Nothing long-lived travels in a URL: the code is single-use and expires in
 * two minutes, while a custom token would stay redeemable for an hour and
 * would land in the browser's history.
 *
 * `readAuthConfig` decides whether any of this runs at all. The mode is a
 * runtime kill switch, so backing this out is a redeploy with AUTH_MODE=local
 * rather than a code change.
 */

export type AuthMode = 'sso' | 'local'

export interface SsoAuthConfig {
  mode: AuthMode
  /** Origin of the sso deployment, never with a trailing slash. */
  ssoBaseUrl: string
  ssoClientId: string
}

/**
 * Reads one string out of the runtime config.
 *
 * Values arrive as STRINGS in a deployed environment because config.js is
 * produced by envsubst: an unset variable renders as "" and an unsubstituted
 * one keeps its "${...}" shape. Both mean "not configured".
 */
function readConfiguredString(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const trimmed = raw.trim()
  return trimmed.startsWith('${') ? '' : trimmed
}

/**
 * The auth block of the runtime config.
 *
 * Read live from `window` rather than from a snapshot taken at import, the
 * same way `identityResolverBaseUrl` is: a snapshot cannot be varied in a test
 * and would ignore a later replacement of the config object.
 *
 * Only the exact string "sso" selects sso mode, and only when it also carries
 * the base URL and client id the mode needs. Everything else is local, which
 * is the mode that has been serving production all along.
 */
export function readAuthConfig(): SsoAuthConfig {
  const raw = (window as { __APP_CONFIG__?: { auth?: Record<string, unknown> } }).__APP_CONFIG__
    ?.auth
  // A configured base URL may or may not carry a trailing slash, and every URL
  // built from it adds its own, so it comes off exactly once, here.
  const ssoBaseUrl = readConfiguredString(raw?.ssoBaseUrl).replace(/\/+$/, '')
  const ssoClientId = readConfiguredString(raw?.ssoClientId)
  const wantsSso = readConfiguredString(raw?.mode) === 'sso'
  return {
    mode: wantsSso && ssoBaseUrl !== '' && ssoClientId !== '' ? 'sso' : 'local',
    ssoBaseUrl,
    ssoClientId,
  }
}

/** Bytes of entropy behind the PKCE verifier, per RFC 7636's 32-octet advice. */
const VERIFIER_BYTES = 32

/** Bytes of entropy behind `state` and `nonce`. */
const HANDLE_BYTES = 16

/** The redirect URI registered for this client with natlink-sso. */
export const SSO_CALLBACK_PATH = '/auth/callback'

export function ssoRedirectUri(): string {
  return `${window.location.origin}${SSO_CALLBACK_PATH}`
}

export interface AuthorizeUrlParams {
  ssoBaseUrl: string
  clientId: string
  redirectUri: string
  state: string
  nonce: string
  codeChallenge: string
}

/**
 * The URL the browser is handed to log in.
 *
 * natlink-sso routes on the bare `#login` fragment and reads the OIDC fields
 * from the query, so the fragment stays last and stays empty of parameters.
 */
export function buildAuthorizeUrl(params: AuthorizeUrlParams): string {
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: params.clientId,
    scope: 'openid',
    redirect_uri: params.redirectUri,
    state: params.state,
    nonce: params.nonce,
    code_challenge: params.codeChallenge,
    code_challenge_method: 'S256',
  })
  return `${params.ssoBaseUrl}/?${query.toString()}#login`
}

/**
 * The URL that ends the sso session.
 *
 * sso reads its own stored session id and returns the browser to the
 * `logoutRedirect` registered for this client, so nothing about where the user
 * lands is decided here.
 */
export function buildSsoLogoutUrl(config: SsoAuthConfig): string {
  const query = new URLSearchParams({ client_id: config.ssoClientId })
  return `${config.ssoBaseUrl}/?${query.toString()}#logout`
}

function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

export function createCodeVerifier(): string {
  return randomBase64Url(VERIFIER_BYTES)
}

/**
 * The S256 challenge for a verifier.
 *
 * `crypto.subtle` needs a secure context, which https and localhost both are;
 * there is no environment where this app runs that is neither.
 */
export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(new Uint8Array(digest))
}

/**
 * Keeps a return location on this origin.
 *
 * The value travels through a query parameter and sessionStorage, so treating
 * it as a URL would turn the callback into an open redirect: "//evil.example"
 * is a protocol-relative absolute URL rather than a path, and a leading "/\"
 * is read the same way by some parsers. Only a single-slash relative path
 * survives; everything else lands on the home page.
 *
 * What it deliberately does NOT chase: a percent-encoded "%2f%2f", or an
 * embedded tab or newline that some URL parsers strip before deciding a value
 * is protocol-relative. Both are inert here because the only consumer is
 * TanStack's `navigate({ href })`, which resolves the value through
 * `new URL(pathname, origin)` against this origin and never assigns
 * `location.href`. A second consumer that DID assign a location would need
 * this function to grow those checks first.
 */
export function sanitizeReturnTo(raw: string | undefined): string {
  if (typeof raw !== 'string') return '/'
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return '/'
  return raw
}

const TOKEN_PATH = '/api/v1/token'

const LOGOUT_PATH = '/api/v1/logout'

/**
 * Caps the logout call. The user has already asked to sign out, so nothing may
 * hold the navigation to sso for longer than a hung request deserves.
 */
const LOGOUT_TIMEOUT_MS = 5000

/**
 * Caps the code exchange. The callback page can do nothing until it answers,
 * so a hung sso has to become a visible, retryable failure rather than a
 * spinner with no end.
 */
const EXCHANGE_TIMEOUT_MS = 10000

/**
 * How long the callback waits for the signed-in session to become visible in
 * `authStore` after Firebase has accepted the custom token.
 *
 * Generous on purpose: the alternative to waiting is navigating into a route
 * guard that still reads signed-out, which sends the user back to sign-in and
 * straight into another login.
 */
const SIGN_IN_SETTLE_TIMEOUT_MS = 10000

/**
 * Prefix of the sessionStorage entry that binds a callback to the tab that
 * started the login.
 *
 * `sessionStorage` rather than `localStorage` because the claim being made is
 * exactly "this tab asked for this": a second tab holding its own half-started
 * login must not be able to complete the first tab's exchange, and neither may
 * a later visit reuse a state it finds lying around.
 */
const STORAGE_PREFIX = 'sso_login:'

/**
 * When the user last asked to sign out, so a page load that happens in the
 * middle of a sign-out does not hand them straight back to sso.
 *
 * Without it, sign-out is a race the user can lose: the local Firebase
 * sign-out flips the auth store, the app navigates to `/sign-in`, and that
 * page starts a fresh login whose navigation competes with the one to sso's
 * logout. If the login wins, sso still holds its session and signs the user
 * straight back in. And even when logout wins, sso returns the tab to
 * `/sign-in`, which would then start a login on arrival.
 *
 * A TIMESTAMP rather than a one-shot flag, in `localStorage` rather than
 * `sessionStorage`, because a sign-out is not one page load in one tab. It is
 * at least two loads of `/sign-in` in the tab signing out -- one per hop above
 * -- and every other tab of the app is still mounted with a session that is
 * about to disappear, so each of those would otherwise start its own login
 * while the logout is in flight. A read that cleared the marker would let the
 * next hop through; a per-tab flag would leave the other tabs uncovered.
 *
 * The shape is claim-web's (`markSignedOut` / `isSignedOut` /
 * `clearSignedOut` in domains/outfitter/claim-web/src/auth/sso.ts).
 */
const SIGNED_OUT_KEY = 'sso_signed_out_at'

/**
 * How long after a sign-out an automatic login start stays suppressed.
 *
 * Long enough to cover the round trip through sso's logout on a slow
 * connection, short enough that a user who signs out and then decides to sign
 * back in is not left looking at a page that will not do it by itself -- and
 * the Continue button is there for exactly that minute anyway.
 */
const SIGNED_OUT_SUPPRESSION_MS = 60_000

export function markSsoSignedOut(): void {
  try {
    window.localStorage.setItem(SIGNED_OUT_KEY, String(Date.now()))
  } catch {
    // Storage can be unavailable in a locked-down browser. Losing the marker
    // costs one unwanted automatic sign-in, so it must not break sign-out.
  }
}

/**
 * Whether a sign-out is recent enough to suppress an automatic login start.
 *
 * A pure read: it does NOT clear the marker, so every page load belonging to
 * one sign-out sees the same answer. `now` is a parameter so the window can be
 * tested without a fake clock.
 *
 * A timestamp in the future means the clock moved, not that the marker is
 * stale, and the negative difference reads as recent -- suppressing a login
 * the user did not ask for is the harmless direction, and the next login that
 * actually starts clears the marker.
 */
export function isSsoSignedOutRecently(now: number = Date.now()): boolean {
  try {
    const raw = window.localStorage.getItem(SIGNED_OUT_KEY)
    if (raw === null) return false
    const markedAt = Number(raw)
    if (!Number.isFinite(markedAt)) return false
    return now - markedAt < SIGNED_OUT_SUPPRESSION_MS
  } catch {
    return false
  }
}

/**
 * Drops the marker once a login is genuinely on its way, so the suppression
 * lasts exactly as long as the sign-out it belongs to.
 */
function clearSsoSignedOut(): void {
  try {
    window.localStorage.removeItem(SIGNED_OUT_KEY)
  } catch {
    // Same as marking: a browser that refuses storage cannot break a login.
  }
}

interface StoredLogin {
  verifier: string
  nonce: string
  returnTo: string
}

export interface SsoNavigationDeps {
  /** Test seam. Production always navigates the real window. */
  navigate: (url: string) => void
}

const defaultNavigation: SsoNavigationDeps = {
  navigate: (url) => {
    window.location.assign(url)
  },
}

/**
 * Hands the browser to natlink-sso to log in.
 *
 * The verifier, the nonce and where to come back to are stored under the
 * `state` that goes out on the URL, so the callback can prove it is answering
 * this tab's request and nobody else's. The page is left behind, so nothing
 * after this call runs.
 */
export async function beginSsoLogin(
  returnTo?: string,
  deps: SsoNavigationDeps = defaultNavigation,
): Promise<void> {
  const config = readAuthConfig()
  const state = randomBase64Url(HANDLE_BYTES)
  const nonce = randomBase64Url(HANDLE_BYTES)
  const verifier = createCodeVerifier()
  const entry: StoredLogin = { verifier, nonce, returnTo: sanitizeReturnTo(returnTo) }
  const codeChallenge = await pkceChallenge(verifier)

  window.sessionStorage.setItem(`${STORAGE_PREFIX}${state}`, JSON.stringify(entry))
  clearSsoSignedOut()
  deps.navigate(
    buildAuthorizeUrl({
      ssoBaseUrl: config.ssoBaseUrl,
      clientId: config.ssoClientId,
      redirectUri: ssoRedirectUri(),
      state,
      nonce,
      codeChallenge,
    }),
  )
}

/** Hands the browser to natlink-sso to end the sso session. */
export function navigateToSsoLogout(deps: SsoNavigationDeps = defaultNavigation): void {
  deps.navigate(buildSsoLogoutUrl(readAuthConfig()))
}

export interface EndSsoSessionRowDeps {
  /** Test seam. Production always uses the real fetch. */
  fetch: typeof globalThis.fetch
}

/**
 * Tells natlink-sso that the session this browser holds has ended, presenting
 * the Firebase ID token as proof that the caller owns it.
 *
 * The `#logout` page sso is about to be handed can only end the row when sso's
 * own browser session still exists on the sso origin, and that session can be
 * long gone while this app's Firebase session is fine. Without this call the row
 * would survive the sign-out and every other relying party would keep treating
 * the session as alive.
 *
 * The sid comes off the credential rather than out of storage so it names the
 * session this credential was actually issued under, which is what stops one
 * relying party from ending a different session of the same user.
 *
 * Best effort throughout: a failure logs and returns, because the alternative is
 * a user who pressed sign out and stayed signed in.
 */
export async function endSsoSessionRow(
  user: User | null | undefined,
  deps: EndSsoSessionRowDeps = { fetch: (input, init) => globalThis.fetch(input, init) },
): Promise<void> {
  if (!user) return
  const config = readAuthConfig()
  if (config.mode !== 'sso') return

  let idToken: string
  let sessionId: string
  try {
    const result = await user.getIdTokenResult()
    const claim = result.claims.sid
    if (typeof claim !== 'string' || claim === '') return
    idToken = result.token
    sessionId = claim
  } catch (err) {
    console.warn('[ssoLogin] could not read the session id off the current credential', err)
    return
  }

  const query = new URLSearchParams({ client_id: config.ssoClientId, sid: sessionId })
  try {
    const response = await deps.fetch(`${config.ssoBaseUrl}${LOGOUT_PATH}?${query.toString()}`, {
      headers: { Authorization: `Bearer ${idToken}` },
      credentials: 'omit',
      signal:
        typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(LOGOUT_TIMEOUT_MS) : undefined,
    })
    if (!response.ok) {
      // A rejected logout is worse than an unreachable one and used to look
      // identical: the row survives while the user believes they are out of
      // every service, and the only remaining chance to end it is sso's own
      // logout page, which needs sso's session on its origin to still exist.
      // The status alone, with no body: an error body from sso could carry the
      // session id or the token straight into the console.
      console.warn(
        `[ssoLogin] sso answered ${response.status} when asked to end the session row`,
      )
    }
  } catch (err) {
    console.warn('[ssoLogin] ending the sso session row failed; signing out locally anyway', err)
  }
}

export type SsoLoginFailureReason = 'invalid_state' | 'exchange_failed' | 'sign_in_failed'

export type SsoLoginOutcome =
  | { kind: 'signed-in'; returnTo: string }
  /**
   * `returnTo` is where the login was headed, on a failure that happened late
   * enough for that to be known: a retry then resumes the journey rather than
   * dropping the user on the home page. It is absent for a state this tab
   * never sent, because nothing about that request is known at all.
   */
  | { kind: 'failed'; reason: SsoLoginFailureReason; returnTo?: string }

export interface CompleteSsoLoginDeps {
  fetch: typeof globalThis.fetch
  signInWithCustomToken: (customToken: string) => Promise<void>
  waitUntilSignedInAs: (uid: string, timeoutMs: number) => Promise<boolean>
}

function productionCompleteDeps(): CompleteSsoLoginDeps {
  return {
    fetch: (input, init) => globalThis.fetch(input, init),
    signInWithCustomToken: async (customToken) => {
      await firebaseSignInWithCustomToken(getFirebaseAuth(), customToken)
    },
    waitUntilSignedInAs: (uid, timeoutMs) => authStore.waitUntilSignedInAs(uid, timeoutMs),
  }
}

/**
 * Finishes the login the callback URL is carrying.
 *
 * Fails closed with a typed reason the callback page can render: there is no
 * outcome where the user is left signed in to something they did not ask for,
 * and no failure that silently looks like success.
 */
export async function completeSsoLogin(
  location: Location,
  deps: CompleteSsoLoginDeps = productionCompleteDeps(),
): Promise<SsoLoginOutcome> {
  const params = new URLSearchParams(location.search)
  const code = params.get('code')
  const state = params.get('state')
  if (!code || !state) return { kind: 'failed', reason: 'invalid_state' }

  const entry = takeStoredLogin(state)
  if (!entry) return { kind: 'failed', reason: 'invalid_state' }

  let exchanged: { customToken: string; uid: string; nonce: string }
  try {
    exchanged = await exchangeCode(code, entry.verifier, deps.fetch)
  } catch (err) {
    console.error('[ssoLogin] the authorization code exchange failed', err)
    return { kind: 'failed', reason: 'exchange_failed', returnTo: entry.returnTo }
  }
  if (exchanged.nonce !== entry.nonce) {
    // The nonce ties the token response to the authorize request this tab
    // made. A mismatch means the response answers some other request.
    console.error('[ssoLogin] the token response carried a nonce this tab never sent')
    return { kind: 'failed', reason: 'exchange_failed', returnTo: entry.returnTo }
  }

  try {
    await deps.signInWithCustomToken(exchanged.customToken)
  } catch (err) {
    console.error('[ssoLogin] signInWithCustomToken failed for an sso session', err)
    return { kind: 'failed', reason: 'sign_in_failed', returnTo: entry.returnTo }
  }

  // signInWithCustomToken resolves BEFORE the provider's onAuthStateChanged
  // listener has run, so the app's auth store still says signed-out at this
  // point. Returning success here sends the caller into a route guard that
  // reads that stale state, redirects to sign-in, and starts another login --
  // a loop that only ends when the timing happens to land. Waiting for the
  // store is what makes "signed in" mean the app can see it.
  //
  // It waits for the UID the token response names rather than for any
  // session, because a callback for one account can arrive while another
  // account's session is still cached -- and that one is visible in the store
  // the moment this runs, so "any session" would navigate into the app as the
  // previous user and swap under them a moment later.
  if (!(await deps.waitUntilSignedInAs(exchanged.uid, SIGN_IN_SETTLE_TIMEOUT_MS))) {
    console.error('[ssoLogin] the custom token was accepted but its session never became visible')
    return { kind: 'failed', reason: 'sign_in_failed', returnTo: entry.returnTo }
  }
  return { kind: 'signed-in', returnTo: sanitizeReturnTo(entry.returnTo) }
}

/**
 * Reads the stored login and removes it in the same breath.
 *
 * Removing BEFORE the exchange is what makes a re-opened callback URL fail as
 * `invalid_state` rather than replaying a code the server has already spent --
 * which would come back as `invalid_grant` and read like a server fault.
 */
function takeStoredLogin(state: string): StoredLogin | undefined {
  const key = `${STORAGE_PREFIX}${state}`
  let raw: string | null
  try {
    raw = window.sessionStorage.getItem(key)
    window.sessionStorage.removeItem(key)
  } catch {
    return undefined
  }
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw) as Partial<StoredLogin>
    if (typeof parsed.verifier !== 'string' || typeof parsed.nonce !== 'string') return undefined
    return {
      verifier: parsed.verifier,
      nonce: parsed.nonce,
      returnTo: sanitizeReturnTo(parsed.returnTo),
    }
  } catch {
    return undefined
  }
}

async function exchangeCode(
  code: string,
  verifier: string,
  fetchImpl: typeof globalThis.fetch,
): Promise<{ customToken: string; uid: string; nonce: string }> {
  const config = readAuthConfig()
  const response = await fetchImpl(`${config.ssoBaseUrl}${TOKEN_PATH}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      code,
      client_id: config.ssoClientId,
      redirect_uri: ssoRedirectUri(),
      code_verifier: verifier,
    }),
    // The code and the verifier are the whole credential, and this is a
    // cross-origin call: no cookie of ours travels with it.
    credentials: 'omit',
    // A redirect would mean the token endpoint is not where we think it is,
    // and following one would post the verifier somewhere else entirely.
    redirect: 'error',
    // Guarded because AbortSignal.timeout is missing from some test runtimes,
    // and a unit test must not fail over the cap rather than the behaviour.
    signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(EXCHANGE_TIMEOUT_MS) : undefined,
  })
  if (!response.ok) {
    throw new Error(`the sso token endpoint answered ${response.status}`)
  }
  const body = (await response.json()) as {
    customToken?: unknown
    uid?: unknown
    nonce?: unknown
  }
  if (typeof body.customToken !== 'string' || body.customToken === '') {
    throw new Error('the sso token response carried no custom token')
  }
  // A response with no uid cannot be completed: the settle wait would have no
  // user to wait for, and falling back to "any session" is the very bug that
  // wait exists to prevent.
  if (typeof body.uid !== 'string' || body.uid === '') {
    throw new Error('the sso token response carried no uid')
  }
  return {
    customToken: body.customToken,
    uid: body.uid,
    // An absent nonce fails the comparison in completeSsoLogin, which is the
    // right answer: a response that cannot be tied to this tab's request is
    // not usable.
    nonce: typeof body.nonce === 'string' ? body.nonce : '',
  }
}
